import { useState, useEffect } from 'react';
import { createDonation, queryFamilyForDonation } from "../../service/DonationApi";
import { queryCategoryConfig } from "../../service/UtilApi";
import { Helmet } from "react-helmet-async";
import { Navbar, Footer, Loading, Breadcrumbs } from "../../components/shared";
import { useCheckAccess, getCurrentDateForTransactionId, formatINR, getSubareaNameTamilWithoutArea } from "../../utils/Utilities";
import { useNavigate } from "react-router-dom";
import '../general/General.css';
import { statusExplanation } from '../general/DropDownValuesConfig';
import { getUsedAddressArea } from "../../service/AddressApi";
import { queryFamiliesByArea } from "../../service/FamilyQueryApi";
// Function to get today's date
const getTodayDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// Function to generate unique transaction ID
const generateTransactionId = ({ familyId, category, subcategory, uniqueOption, amount, date }) => {
  if (!familyId || !category || !subcategory) {
    throw new Error("Missing required input to generate transaction ID");
  }
  const baseId = `${familyId}_${category}_${subcategory}`.replace(/\s+/g, '');
  if (uniqueOption === 'familyId') {
    return baseId;
  }
  const currentDate = getCurrentDateForTransactionId(date);
  return `${baseId}_${currentDate}_${amount}`;
};


// Main component for donation creation
const DonationCreate = () => {
  useCheckAccess('DonationRW');

  const [categories, setCategories] = useState([]);
  const [categoryName, setCategoryName] = useState('');
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [familyId, setFamilyId] = useState('');
  const [mobile, setMobile] = useState('');
  const [donationDate, setDonationDate] = useState(getTodayDate());
  const [transactionMode, setTransactionMode] = useState('cash');
  const [externalTransactionReference, setExternalTransactionReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [tempAddressTamil, setTempAddressTamil] = useState('');
  const [permanentAddressTamil, setPermanentAddressTamil] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [searchError, setSearchError] = useState('');
  const [validationError, setValidationError] = useState('');
  const navigate = useNavigate();
  const [searching, setSearching] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingDonationData, setPendingDonationData] = useState(null);
  const [selfSponsoredWarning, setSelfSponsoredWarning] = useState(false);
  const [showGuestOption, setShowGuestOption] = useState(false);

  // 🔍 Address search states
  const [district, setDistrict] = useState('');
  const [block, setBlock] = useState('');
  const [area, setArea] = useState('');
  const [subarea, setSubarea] = useState('');
  const [pincode, setPincode] = useState('');

  const [areasMaster, setAreasMaster] = useState([]);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [areaFamilies, setAreaFamilies] = useState([]);
  const [tempAreaFamilies, setTempAreaFamilies] = useState([]);
  const [addressError, setAddressError] = useState('');
  useEffect(() => {
    const load = async () => {
      const res = await getUsedAddressArea();
      setAreasMaster(res.addressAreaList || []);
    };
    load();
  }, []);
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await queryCategoryConfig();
        if (response?.categoriesList?.length > 0) {
          setCategories(response.categoriesList);
        } else {
          setCategoriesError("No categories found");
        }
      } catch {
        setCategoriesError("Failed to load categories");
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  const selectedMember = searchResult?.members?.find(m => m.uid === selectedMemberId);
  useEffect(() => {
    if (!selectedMember) return;
    if (categories.length === 0) return;

    const isOtherKootam = searchResult?.family?.kootamName === 'பிற';

    const filteredCategories = categories.filter(c => {
      const hasTax = c.categoryName.includes('Tax');
      return !(isOtherKootam && hasTax);
    });

    const defaultCategory =
      filteredCategories.find(c => c.isDefault) ||
      filteredCategories[0];

    setCategoryName(defaultCategory?.categoryName || '');

  }, [selectedMember, categories]);
  const selectedCategory = categories.find(cat => cat.categoryName === categoryName);

  const last5TempleDonations = searchResult?.donations
    ?.sort((a, b) => (b.transactionDate?._seconds || 0) - (a.transactionDate?._seconds || 0))
    ?.slice(0, 5) || [];
  const sortedSubcategories = selectedCategory?.subcategories
    ? [...selectedCategory.subcategories].sort((a, b) => a.name.localeCompare(b.name))
    : [];

  const handleSelectFamilyFromAddress = async (id) => {
    setFamilyId(id);
    await handleSearchFamily({ familyId: id });
  };
  const handleSelectAddress = async (item) => {
    setAddressSuggestions([]);
    setSearchResult(null);
    setAddressError('');

    setSearching(true);

    try {
      const response = await queryFamiliesByArea({
        district: item.district_tamil,
        block: item.block_tamil,
        areaId: item.id,
        subareaId: item.subArea?.id,
        pageNumber: 1,
        pageSize: 20
      });

      if (!response?.families?.length) {
        setAddressError("❌ இந்த முகவரிக்கு குடும்பம் இல்லை");
        setAreaFamilies([]);
      } else {
        setAreaFamilies(response.families);
      }
      if (!response?.tempFamilies?.length) {
        setTempAreaFamilies([]);
      } else {
        setTempAreaFamilies(response.tempFamilies);
      }
    } finally {
      setSearching(false);
    }
  };
  const handleAddressTyping = (value, field) => {
    let newDistrict = district;
    let newBlock = block;
    let newArea = area;
    let newSubarea = subarea;
    let newPincode = pincode;

    if (field === 'district') newDistrict = value;
    if (field === 'block') newBlock = value;
    if (field === 'area') newArea = value;
    if (field === 'subarea') newSubarea = value;
    if (field === 'pincode') newPincode = value;

    setDistrict(newDistrict);
    setBlock(newBlock);
    setArea(newArea);
    setSubarea(newSubarea);
    setPincode(newPincode);

    setSearchResult(null);
    setAreaFamilies([]);
    setTempAreaFamilies([]);
    setAddressError('');

    if (!newDistrict && !newBlock && !newArea && !newSubarea && !newPincode) {
      setAddressSuggestions([]);
      setAddressError('');
      return;
    }

    const filtered = areasMaster.filter(a => {
      const subMatch = a.subAreas?.some(sa =>
        (`${sa.name} ${sa.name_tamil}`)
          .toLowerCase()
          .includes(newSubarea.toLowerCase())
      );

      return (
        (!newDistrict || `${a.district} ${a.district_tamil}`.toLowerCase().includes(newDistrict.toLowerCase())) &&
        (!newBlock || `${a.block} ${a.block_tamil}`.toLowerCase().includes(newBlock.toLowerCase())) &&
        (!newArea || `${a.name} ${a.name_tamil}`.toLowerCase().includes(newArea.toLowerCase())) &&
        (!newSubarea || subMatch) &&
        (!newPincode || a.pincode.includes(newPincode))
      );
    });

    if (filtered.length === 0) {
      setAddressSuggestions([]);
      setAddressError("❌ முகவரி கிடைக்கவில்லை");
    } else {
      setAddressSuggestions(filtered.slice(0, 10));
    }
  };
  const handleSearchFamily = async (override = {}) => {
    setSearchResult(null);
    setSearchError('');
    setAddressError('');
    setSelectedMemberId('');
    setAddressSuggestions([]);
    setAreaFamilies([]);
    setTempAreaFamilies([]);
    setSearching(true);
    setSelectedSubcategories([]); // 🔁 Reset subcategories
    setCategoryName('');          // 🔁 Reset category
    setPermanentAddressTamil('');
    setTempAddressTamil('');
    const finalFamilyId = override.familyId ?? familyId;
    const finalMobile = override.mobile ?? mobile;

    // ✅ NEW validation (correct)
    if (!finalFamilyId && !finalMobile) {
      setValidationError("தயவுசெய்து குடும்ப எண் அல்லது Mobile ஒன்றை உள்ளிடவும்.");
      setSearching(false);
      return;
    }

    // optional: format validation
    if (finalFamilyId && !/^\d{1,4}$/.test(finalFamilyId)) {
      setValidationError("குடும்ப எண் 1 முதல் 4 இலக்க எண் இருக்க வேண்டும்.");
      setSearching(false);
      return;
    }

    if (finalMobile && !/^\d{10}$/.test(finalMobile)) {
      setValidationError("மொபைல் எண் 10 இலக்க எண் இருக்க வேண்டும்.");
      setSearching(false);
      return;
    }

    setValidationError('');

    try {

      const response = await queryFamilyForDonation({
        familyId: finalFamilyId,
        mobile: finalMobile
      });

      if (response?.family || response?.members?.length > 0) {
        setSearchResult(response);
        if (response.family?.id) {
          setFamilyId(response.family.id);
          const { tempAddressAreaId, tempSubareaId, permanentAddressAreaId, permanentSubareaId } = response.family;
          if (tempAddressAreaId && tempSubareaId) {
            const name = await getSubareaNameTamilWithoutArea(tempAddressAreaId, tempSubareaId);
            setTempAddressTamil(name);
          }
          if (permanentAddressAreaId && permanentSubareaId) {
            const name = await getSubareaNameTamilWithoutArea(permanentAddressAreaId, permanentSubareaId);
            setPermanentAddressTamil(name);
          }
        }
      } else {
        setSearchError("குடும்பம் எதுவும் கிடைக்கவில்லை.");
        setShowGuestOption(true);
      }
    } catch (err) {
      setSearchError("Error fetching family data.");
      console.error(err);
    } finally {
      setSearching(false);
    }
  };

  const handleAddDonation = async () => {
    if (!selectedMember) {
      alert("தயவுசெய்து ஒரு குடும்ப உறுப்பினரை தேர்வு செய்யவும்.");
      return;
    }

    if (!categoryName) {
      alert("தயவுசெய்து ஒரு வகையை தேர்வு செய்யவும்.");
      return;
    }

    if (selectedSubcategories.length === 0) {
      alert("தயவுசெய்து ஒரு துணை வகையைத் தேர்ந்தெடுக்கவும்.");
      return;
    }

    // ❗ Validate amounts
    for (const sub of selectedSubcategories) {

      const fullSub = selectedCategory.subcategories.find(s => s.name === sub.subcategory);

      const isReceiptRequired = fullSub?.isReceiptNumberRequired !== false;



      if (
        sub.amount === '' ||
        isNaN(sub.amount) ||
        (isReceiptRequired && !sub.receiptNo) ||
        (!isReceiptRequired && !sub.noReceipt && !sub.receiptNo)
      ) {
        alert("தயவுசெய்து அனைத்து துணை வகைகளுக்கும் ரசீது எண் அல்லது 'ரசீது எண் இல்லை' என்பதைத் தேர்ந்தெடுக்கவும்.");
        return;
      }

      const matchedDonations = searchResult?.donations?.filter(d =>
        d.category === selectedCategory.description &&
        d.subcategory === fullSub.description &&
        (fullSub.uniqueOption === 'familyId' || d.familyMemberFirstName === selectedMember.firstName)
      ) || [];

      const totalDonated = matchedDonations.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
      const proposedTotal = totalDonated + parseFloat(sub.amount);

      if (fullSub.amount && proposedTotal > fullSub.amount) {
        alert(`❗ துணை வகை "${fullSub.description}" இல் அதிகபட்ச தொகை ₹${fullSub.amount}. ஏற்கனவே செலுத்தியது ₹${totalDonated}. நீங்கள் ₹${parseFloat(sub.amount)} செலுத்த முயற்சிக்கிறீர்கள்.`);
        return;
      }

    }
    // ✅ Ask for confirmation
    // Check if any selected subcategory allows self-sponsored but user didn’t select it
    const needsSelfSponsoredPrompt = selectedSubcategories.some(sub => {
      const fullSub = selectedCategory.subcategories.find(s => s.name === sub.subcategory);
      return fullSub?.allowSelfSponsored && !sub.isSelfSponsored;
    });

    setSelfSponsoredWarning(needsSelfSponsoredPrompt);
    setPendingDonationData({
      familyId,
      familyMemberId: selectedMember.uid,
      familyMemberFirstName: selectedMember.firstName,
      familyMemberLastName:
        selectedMember.lastName &&
          selectedMember.lastName !== '..'
          ? selectedMember.lastName.trim()
          : '',
      permanentSubareaId: searchResult?.family?.permanentSubareaId || '',
      tempSubareaId: searchResult?.family?.tempSubareaId || '',
      permanentAddressAreaId: searchResult?.family?.permanentAddressAreaId || '',
      tempAddressAreaId: searchResult?.family?.tempAddressAreaId || '',
      category: categoryName,
      categoryDescription: selectedCategory?.description || '',
      date: donationDate,
      externalTransactionReference,
      transactionMode,
      subcategories: selectedSubcategories.map(sub => {
        const fullSub = selectedCategory.subcategories.find(s => s.name === sub.subcategory);
        return {
          subcategory: sub.subcategory,
          subCategoryDescription: fullSub?.description || '',
          amount: Number(sub.amount),
          receiptNo: sub.noReceipt ? '' : sub.receiptNo,
          isSelfSponsored: !!sub.isSelfSponsored,
          transactionId: generateTransactionId({
            familyId,
            category: categoryName,
            subcategory: sub.subcategory,
            uniqueOption: sub.uniqueOption,
            amount: sub.amount,
            date: donationDate
          }),
        };
      })
    });
    setShowConfirmationModal(true);
  };



  const handleSubcategoryChange = (subcategory, checked) => {
    setSelectedSubcategories(prevState => {
      if (checked) {
        // Find matched donations for this subcategory
        const matchedDonations = searchResult?.donations?.filter(d =>
          d.category === selectedCategory?.description &&
          d.subcategory === subcategory.description &&
          (
            subcategory.uniqueOption === 'familyId' ||
            subcategory.uniqueOption === 'familyId_date' ||
            d.familyMemberFirstName === selectedMember?.firstName
          )
        ) || [];

        const totalDonated = matchedDonations.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
        const remaining = Math.max((subcategory.amount || 0) - totalDonated, 0);

        return [
          ...prevState,
          {
            subcategory: subcategory.name,
            amount: subcategory.uniqueOption === 'familyId' ? subcategory.amount : remaining.toFixed(0),
            isSelfSponsored: false,
            noReceipt: subcategory.isReceiptNumberRequired === false
          }
        ];
      } else {
        return prevState.filter(sub => sub.subcategory !== subcategory.name);
      }
    });
  };


  const handleAmountChange = (subcategoryName, amount) => {
    // Keep value as string so user can type decimals freely
    setSelectedSubcategories(prevState =>
      prevState.map(sub =>
        sub.subcategory === subcategoryName
          ? { ...sub, amount: amount }  // <-- store as string
          : sub
      )
    );
  };

  const handleReceiptChange = (subcategoryName, receiptNo) => {
    setSelectedSubcategories(prevState =>
      prevState.map(sub =>
        sub.subcategory === subcategoryName
          ? { ...sub, receiptNo }
          : sub
      )
    );
  };

  const toggleSelfSponsored = (subcategoryName, checked) => {
    setSelectedSubcategories(prev =>
      prev.map(sub =>
        sub.subcategory === subcategoryName
          ? { ...sub, isSelfSponsored: checked }
          : sub
      )
    );
  };
  const toggleNoReceipt = (subcategoryName, checked) => {
    setSelectedSubcategories(prev =>
      prev.map(sub =>
        sub.subcategory === subcategoryName
          ? { ...sub, noReceipt: checked, receiptNo: checked ? '' : sub.receiptNo }
          : sub
      )
    );
  };
  const hasDirectInput = familyId || mobile;
  return (
    <>
      <Navbar />
      <Helmet>
        <title>ஸ்ரீ பொன்னாச்சி அம்மன் திருக்கோவில்கள் | Donation Create</title>
        <link rel="canonical" href="/donation/create" />
      </Helmet>

      <div className="general_parent">
        <div className="general_container">
          <div className="general_body">
            <Breadcrumbs /><br />
            <h2>நன்கொடை உருவாக்கு</h2>

            <form
              className="auth_element"
              onSubmit={(e) => {
                e.preventDefault();
                handleSearchFamily();
              }}
            >
              <label className="auth_label">குடும்ப எண்:</label>
              <input value={familyId} onChange={e => setFamilyId(e.target.value)}
                className="auth_input"
                inputMode="numeric"
                maxLength="4"
              />

              <label className="auth_label">மொபைல்:</label>
              <input value={mobile} onChange={e => setMobile(e.target.value)}
                className="auth_input"
                inputMode="numeric"
                maxLength="10" />

              {searching ? (
                <Loading />
              ) : (
                <button onClick={handleSearchFamily}>தேடு</button>
              )}

              {validationError && <p style={{ color: 'red' }}>{validationError}</p>}
            </form>
            {!hasDirectInput && (
              <div className="auth_element">
                <h3>முகவரி மூலம் தேடல்</h3>

                <input placeholder="மாவட்டம்"
                  className="auth_input"
                  value={district}
                  onChange={e => handleAddressTyping(e.target.value, 'district')} />

                <input placeholder="தாலுகா"
                  className="auth_input"
                  value={block}
                  onChange={e => handleAddressTyping(e.target.value, 'block')} />

                <input placeholder="தபால்"
                  className="auth_input"
                  value={area}
                  onChange={e => handleAddressTyping(e.target.value, 'area')} />

                <input placeholder="ஊர்"
                  className="auth_input"
                  value={subarea}
                  onChange={e => handleAddressTyping(e.target.value, 'subarea')} />

                <input placeholder="Pincode"
                  className="auth_input"
                  value={pincode}
                  onChange={e => handleAddressTyping(e.target.value, 'pincode')} />
              </div>
            )}

            {searchError && <p style={{ color: 'red' }}>{searchError}</p>}
            {addressError && <p style={{ color: 'red' }}>{addressError}</p>}
            {!hasDirectInput && addressSuggestions.length > 0 && (
              <div className="details-card">
                {addressSuggestions.flatMap(a => {
                  const filteredSub = a.subAreas?.filter(sa =>
                    !subarea ||
                    (`${sa.name} ${sa.name_tamil}`).toLowerCase().includes(subarea.toLowerCase())
                  );

                  return filteredSub?.length > 0
                    ? filteredSub.map(sa => ({ ...a, subArea: sa }))
                    : (!subarea ? [{ ...a, subArea: null }] : []);
                }).map(item => (
                  <div
                    key={`${item.id}_${item.subArea?.id || 'x'}`}
                    style={{ cursor: 'pointer', padding: '6px' }}
                    onClick={() => handleSelectAddress(item)}
                  >
                    <strong>{item.subArea?.name_tamil || item.name_tamil}</strong>
                    <br />
                    {item.name_tamil}, {item.block_tamil}, {item.district_tamil}
                    <hr />
                  </div>
                ))}
              </div>
            )}
            {!hasDirectInput && areaFamilies.length > 0 && (
              <div className="details-card">
                <h4>குடும்பங்கள்</h4>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>எண்</th>
                        <th>பெயர்</th>
                        <th>வரி</th>
                        <th>திருப்பணி</th>
                      </tr>
                    </thead>

                    <tbody>
                      {areaFamilies.map((f) => (
                        <tr
                          key={f.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSelectFamilyFromAddress(f.id)}
                          title="Click to select family"
                        >

                          <td><strong>{f.id}</strong></td>

                          <td>
                            {f.familyHeadFirstName} {f.familyHeadLastName}{f.familyName && (' (' + f.familyName + ')')}
                            📱 {f.familyHeadMobile}
                            {f.familyHeadMobile2 ? ` / ${f.familyHeadMobile2}` : ''}
                            <br /> {f.familyOrigin && ('பூர்வீகம்: ' + f.familyOrigin)}
                          </td>

                          <td>
                            {f.pongalTaxUptoYear || '-'}
                          </td>

                          <td>
                            {f.chinnaKovil2025TempleConstructionTotal
                              ? formatINR(f.chinnaKovil2025TempleConstructionTotal)
                              : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {!hasDirectInput && tempAreaFamilies.length > 0 && (
              <div className="details-card">
                <h4>தற்காலிக முகவரி குடும்பங்கள்</h4>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>எண்</th>
                        <th>பெயர்</th>
                        <th>வரி</th>
                        <th>திருப்பணி</th>
                      </tr>
                    </thead>

                    <tbody>
                      {tempAreaFamilies.map((f) => (
                        <tr
                          key={f.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleSelectFamilyFromAddress(f.id)}
                          title="Click to select family"
                        >

                          <td><strong>{f.id}</strong></td>

                          <td>
                            {f.familyHeadFirstName} {f.familyHeadLastName}{f.familyName && (' (' + f.familyName + ')')}
                            📱 {f.familyHeadMobile}
                            {f.familyHeadMobile2 ? ` / ${f.familyHeadMobile2}` : ''}
                            <br /> {f.familyOrigin && ('பூர்வீகம்: ' + f.familyOrigin)}
                          </td>

                          <td>
                            {f.pongalTaxUptoYear || '-'}
                          </td>

                          <td>
                            {f.chinnaKovil2025TempleConstructionTotal
                              ? formatINR(f.chinnaKovil2025TempleConstructionTotal)
                              : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {showGuestOption && (
              <div style={{ marginTop: "1rem", color: "blue" }}>
                <p>நீங்கள் விருந்தினர் நன்கொடை உருவாக்க விரும்புகிறீர்களா?</p>
                <button
                  onClick={() => {
                    if (mobile) {
                      navigate(`/donation/create-guest?mobile=${mobile}`);
                    } else {
                      navigate("/donation/create-guest");
                    }
                  }}
                  style={{ marginTop: "0.5rem" }}
                >
                  விருந்தினர் நன்கொடை உருவாக்கு
                </button>

              </div>
            )}

            {searchResult?.family && (
              <>
                <div className="details-card">

                  <div className="detail-item">
                    <span className="detail-label"><strong>குடும்ப எண்:</strong></span> <span className="detail-value">{searchResult.family.id}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label"><strong>கூட்டம்:</strong></span><span className="detail-value"> {searchResult.family.kootamName}
                    </span>
                  </div>
                  {
                    searchResult.family.customGroup && (
                      <div className="detail-item">
                        <span className="detail-label"><strong>மற்ற குழு:</strong> </span>
                        <span className="detail-value">
                          {searchResult.family.customGroup}
                        </span>
                      </div>
                    )
                  }
                  <div className="detail-item">
                    <span className="detail-label"><strong>குடும்ப தலைவர்:</strong> </span><span className="detail-value">{searchResult.family.familyHeadFirstName} {searchResult.family.familyHeadLastName}
                    </span>
                  </div>
                  {
                    searchResult.family.familyName && (
                      <div className="detail-item">
                        <span className="detail-label"><strong>குடும்பப் பெயர்:</strong> </span>
                        <span className="detail-value">
                          {searchResult.family.familyName}
                        </span>
                      </div>
                    )
                  }
                  {
                    searchResult.family.familyOrigin && (
                      <div className="detail-item">
                        <span className="detail-label"><strong>பூர்வீகம்:</strong> </span>
                        <span className="detail-value">
                          {searchResult.family.familyOrigin}
                        </span>
                      </div>
                    )
                  }
                  {searchResult.family.tempAddressName_tamil && (
                    <div className="detail-item">
                      <span className="detail-label"><strong>தற்காலிக முகவரி:</strong> </span>
                      <span className="detail-value">
                        {tempAddressTamil && (tempAddressTamil + ', ')}
                        {searchResult.family.tempAddressName_tamil}, {searchResult.family.tempAddressBlock_tamil}, {searchResult.family.tempAddressDistrict_tamil}
                      </span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label"><strong>நிரந்தர முகவரி:</strong> </span>
                    <span className="detail-value">
                      {permanentAddressTamil && (permanentAddressTamil + ', ')}
                      {searchResult.family.permanentAddressName_tamil}, {searchResult.family.permanentAddressBlock_tamil}, {searchResult.family.permanentAddressDistrict_tamil}
                    </span>
                  </div>

                  <div className="detail-item">
                    <span className="detail-label"><strong>நிலை:</strong></span>

                    <span style={{ color: ['draft', 'inactive', 'pending'].includes(searchResult.family.status) ? 'red' : 'inherit' }}>
                      {searchResult.family.status || ""}
                      {statusExplanation[searchResult.family.status] || "இது ஒரு அறியப்படாத நிலை."}
                    </span>
                  </div>
                  {
                    searchResult.family.keyContact && (
                      <div className="detail-item">
                        <span className="detail-label"><strong>முக்கிய தொடர்பு:</strong> </span>
                        <span className="detail-value">
                          {searchResult.family.keyContact ? '✅' : '❌'}
                        </span>
                      </div>
                    )
                  }
                  {
                    searchResult.family.pongalTaxUptoYear && (
                      <div className="detail-item">
                        <span className="detail-label"><strong>பொங்கல் வரி:</strong> </span>
                        <span className="detail-value">
                          {searchResult.family.pongalTaxUptoYear}
                        </span>
                      </div>
                    )
                  }
                  {
                    searchResult.family.chinnaKovil2025TempleConstructionTotal && (
                      <div className="detail-item">
                        <span className="detail-label"><strong>திருப்பணி:</strong> </span>
                        <span className="detail-value">
                          {formatINR(searchResult.family.chinnaKovil2025TempleConstructionTotal)}
                        </span>
                      </div>
                    )
                  }
                  {last5TempleDonations.length > 0 && (
                    <>
                      <h4>சமீபத்திய நன்கொடைகள்:</h4>
                      <div className="table-container">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>வகை</th>
                              <th>தேதி</th>
                              <th>தொகை</th>
                            </tr>
                          </thead>

                          <tbody>
                            {last5TempleDonations.map((txn, idx) => (
                              <tr
                                key={idx}
                                style={{ cursor: 'pointer' }}
                              >
                                <td>
                                  {txn.category + ' '}{txn.subcategory}
                                </td>

                                <td>
                                  {new Date(txn.transactionDate?._seconds * 1000).toLocaleDateString('ta-IN')}
                                </td>

                                <td>{formatINR(txn.amount)}

                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                  <button
                    style={{ marginTop: '1rem' }}
                    onClick={() => navigate(`/donation/view/${searchResult.family.id}`)}
                  >
                    குடும்ப நன்கொடைப் பதிவு பார்க்க
                  </button>
                </div>
                {searchResult.family.status !== 'active' && (
                  <div style={{ marginTop: '1rem', color: 'red', fontWeight: 'bold' }}>
                    ⚠️ குடும்ப விவரங்கள் முலுமையாக இல்லை. தயவுசெய்து விண்ணப்பப் படிவத்தை பூர்த்தி செய்யவும்.
                    <br></br>
                  </div>
                )}
              </>
            )}


            {searchResult?.members?.length > 0 && (
              <div className="auth_element" style={{ marginTop: "1rem" }}>
                <h3>குடும்ப உறுப்பினர்கள்:</h3>
                <br />
                {searchResult.members.map(member => (
                  <div key={member.uid}>
                    <label className='auth_radio'>
                      <input
                        type="radio"
                        name="selectedMember"
                        value={member.uid}
                        checked={selectedMemberId === member.uid}
                        onChange={() => setSelectedMemberId(member.uid)}
                      />
                      {member.firstName} {member.lastName}{(member.mobile || member.mobile2) ? ` - 📱 ${member.mobile || ''}${member.mobile2 ? (member.mobile ? ' / ' : '') + member.mobile2 : ''}` : ''}
                    </label>
                    {member.status !== 'active' && (
                      <div style={{ color: 'red', marginLeft: '1.5rem', fontSize: '0.9rem' }}>
                        ⚠️ ஆதார் விவரங்கள் இல்லை
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {selectedMember && (
              <div className="details-card">
                <div className="detail-item">
                  <span className="detail-label"><strong>நன்கொடை வகை:</strong></span>
                  {categoriesLoading ? (
                    <Loading />
                  ) : categoriesError ? (
                    <p style={{ color: 'red' }}>{categoriesError}</p>
                  ) : (
                    <select
                      value={categoryName}
                      onChange={(e) => {
                        setCategoryName(e.target.value);
                        setSelectedSubcategories([]);
                      }}
                      className="auth_select"
                    >
                      <option value="">-- வகையைத் தேர்ந்தெடுக்கவும் --</option>
                      {categories
                        .filter(cat => {
                          // If kootam is பிற and category name or description contains "Tax", exclude it
                          const isOtherKootam = searchResult?.family?.kootamName === 'பிற';
                          const hasTax = cat.categoryName.includes('Tax');
                          return !(isOtherKootam && hasTax);
                        })
                        .map(cat => (
                          <option key={cat.categoryName} value={cat.categoryName}>
                            {cat.description}
                          </option>
                        ))}
                    </select>

                  )}
                </div>

                {selectedCategory?.subcategories?.length > 0 && (
                  <div className="auth_element">
                    <br />

                    {sortedSubcategories
                      .filter(sub => {
                        const isOtherKootam = searchResult?.family?.kootamName === 'பிற';
                        return !(isOtherKootam && (sub?.isTax || false));
                      })
                      .map(sub => {
                        const selected = selectedSubcategories.find(s => s.subcategory === sub.name) || {};

                        // Define this near where you're mapping subcategories
                        const matchedDonations = searchResult?.donations?.filter(d =>
                          d.category === selectedCategory?.description &&
                          d.subcategory === sub.description &&
                          (
                            sub.uniqueOption === 'familyId' ||
                            sub.uniqueOption === 'familyId_date' ||
                            d.familyMemberFirstName === selectedMember?.firstName
                          )
                        ) || [];

                        const isYearFormat = /^\d{4}$/.test(sub.name);

                        /*const allPaidYears = (
                          searchResult?.donations?.filter(d =>
                            d.category === selectedCategory?.description &&
                            d.subcategory &&
                            /^\d{4}$/.test(d.subcategory) &&
                            sub.uniqueOption === 'familyId'
                          ).map(d => parseInt(d.subcategory, 10))
                        ) || []; */

                        //const maxPaidYear = allPaidYears.length > 0 ? Math.max(...allPaidYears) : null;

                        // Calculate total
                        const totalDonated = matchedDonations.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

                        // Check if donation is allowed

                        const alreadyCompleted = (() => {
                          if (sub.allowSelfSponsored) {
                            return false;
                          }
                          if (sub.amount == null) return false;
                          const requiredAmount = sub.amount || Infinity;

                          // Subcategory is a year (e.g., "2021") and uniqueOption is familyId
                          /* if (sub.uniqueOption === 'familyId' && isYearFormat) {
                             const subYear = parseInt(sub.name, 10);
   
                             if (maxPaidYear && subYear < maxPaidYear) {
                               return true; // Don't allow paying for past years
                             }
   
                             return totalDonated > 0;
                           }*/
                          if (sub.uniqueOption === 'familyId' && isYearFormat) {
                            const isAlreadyPaid = matchedDonations.length > 0;

                            return isAlreadyPaid; // ✅ block only if THAT year is already paid
                          }

                          if (sub.uniqueOption === 'familyId') {
                            return totalDonated > 0;
                          }

                          if (sub.uniqueOption === 'familyId_date') {
                            if (sub.amount == null) return false; // Unlimited – never block
                            return totalDonated >= sub.amount;
                          }

                          return totalDonated >= requiredAmount;
                        })();




                        return (
                          <div key={sub.name} style={{ marginBottom: '0.5rem', opacity: alreadyCompleted ? 0.5 : 1 }}>


                            {!(alreadyCompleted) && (
                              <div className="detail-item">
                                <label className='auth_checkbox'>
                                  <input
                                    type="checkbox"
                                    value={sub.name}
                                    checked={!!selected.subcategory}
                                    onChange={(e) => handleSubcategoryChange(sub, e.target.checked)}
                                    disabled={alreadyCompleted}

                                  />

                                  <span className="auth_checkbox-label">{sub.description}</span>
                                </label>
                              </div>

                            )}

                            {selected.subcategory && !alreadyCompleted && (
                              <div style={{ marginLeft: '1rem' }}>
                                <div className="detail-item">
                                  <label className="detail-label">தொகை: </label>
                                  <input
                                    type="text"
                                    pattern="^(0|[1-9]\d*)(\.\d{1,2})?$"
                                    placeholder="தொகை"
                                    inputMode="numeric"
                                    value={selected.amount}
                                    onChange={(e) => handleAmountChange(sub.name, e.target.value)}
                                    style={{ width: '100px' }}
                                  />
                                </div>
                                <div className="detail-item">
                                  <label className="detail-label">ரசீது எண்:</label>
                                  <input
                                    type="text"
                                    placeholder="1234"
                                    inputMode="decimal"
                                    value={selected.receiptNo}
                                    onChange={(e) => handleReceiptChange(sub.name, e.target.value.toUpperCase())}
                                    style={{ width: '100px' }}
                                  />
                                </div>
                                <div className="detail-item">
                                  <label className="auth_checkbox">
                                    <input
                                      type="checkbox"
                                      checked={!!selected.noReceipt}
                                      onChange={(e) => toggleNoReceipt(sub.name, e.target.checked)}
                                    />
                                    ரசீது எண் இல்லை
                                  </label>
                                </div>
                                {/* ✅ Self-sponsored checkbox */}
                                {sub.allowSelfSponsored && (
                                  <div className="detail-item">
                                    <label className='auth_checkbox'>

                                      <input
                                        type="checkbox"
                                        checked={!!selected.isSelfSponsored}
                                        onChange={(e) => toggleSelfSponsored(sub.name, e.target.checked)}
                                      />
                                      நன்கொடையாக செலுத்தப்பட்டது
                                    </label>
                                  </div>
                                )}
                              </div>
                            )}

                            {matchedDonations.length > 0 && (

                              <>
                                {alreadyCompleted && sub.uniqueOption !== 'familyId' && (<h4>{selectedCategory.description + ' ' + sub.description}</h4>)}

                                <div style={{ color: 'green', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                                  {matchedDonations.map((txn, idx) => (
                                    <div key={idx}>

                                      {alreadyCompleted && sub.uniqueOption === 'familyId' && sub.description + ':'}
                                      {new Date(txn.transactionDate?._seconds * 1000).toLocaleDateString('ta-IN')} {formatINR(txn.amount)}  {txn.receiptNo && ('ரசீது எண்:' + txn.receiptNo)}
                                      -{txn.familyMemberFirstName + ' ' + (txn.familyMemberLastName || '')}
                                    </div>
                                  ))}
                                </div>

                                {sub.uniqueOption !== 'familyId' && (
                                  <strong>மொத்தம்: {formatINR(totalDonated)}</strong>
                                )}
                              </>
                            )}
                            {sub.uniqueOption !== 'familyId' && (<hr />)}


                          </div>

                        );
                      })}
                  </div>
                )}

                {selectedSubcategories.length > 0 && (
                  <div className="auth_element" style={{ marginTop: "1rem" }}>
                    <strong style={{ color: 'red' }}>
                      மொத்த நன்கொடை தொகை: ₹{" "}
                      {selectedSubcategories.reduce((sum, sub) => sum + (parseFloat(sub.amount) || 0), 0)}
                    </strong>
                  </div>
                )}

                <div className="auth_element">
                  <label className="auth_label">நன்கொடை தேதி:</label>
                  <input
                    type="date"
                    value={donationDate}
                    onChange={e => setDonationDate(e.target.value)}
                  />
                </div>

                <div className="auth_element">
                  <label className="auth_label">பரிவர்த்தனை முறை:</label>

                  <div className="auth_radio">
                    <label className="auth_radio-label">
                      <input
                        type="radio"
                        name="transactionMode"
                        value="cash"
                        checked={transactionMode === "cash"}
                        onChange={e => setTransactionMode(e.target.value)}
                      />
                      Cash
                    </label>

                    <label className="auth_radio-label">
                      <input
                        type="radio"
                        name="transactionMode"
                        value="UPI"
                        checked={transactionMode === "UPI"}
                        onChange={e => setTransactionMode(e.target.value)}
                      />
                      UPI (GPay/PhonePay)
                    </label>
                  </div>
                </div>


                <div className="auth_element">
                  <label className="auth_label">பரிவர்த்தனை குறிப்பு:</label>
                  <input
                    type="text"
                    value={externalTransactionReference}
                    onChange={e => setExternalTransactionReference(e.target.value)}
                    className="auth_input"
                    placeholder="உதாரணம் GPay எண்"
                  />
                </div>

                <div className="auth_element" style={{ marginTop: "1rem" }}>
                  {loading ? (
                    <Loading />
                  ) : (
                    <button onClick={handleAddDonation} disabled={loading}
                      className={`auth_submit ${loading ? "disabled_btn" : ""}`}>
                      {loading ? 'சமர்ப்பிக்கிறது...' : 'நன்கொடை சேர்க்கவும்'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {showConfirmationModal && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <h3>நீங்கள் உறுதியாக நன்கொடை சேர்க்க விரும்புகிறீர்களா?</h3>
                  {/* 🧾 Summary Table */}
                  <div style={{ marginTop: '1rem', border: '1px solid #ccc', borderRadius: '8px', padding: '1rem', background: '#fafafa' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f0f0f0' }}>
                          <th style={{ border: '1px solid #ddd', padding: '6px' }}>வகை</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>தொகை</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px' }}>ரசீது</th>
                          <th style={{ border: '1px solid #ddd', padding: '6px' }}>செலவிட்டது</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingDonationData.subcategories.map((sub, idx) => (
                          <tr key={idx}>
                            <td style={{ border: '1px solid #ddd', padding: '6px' }}>{pendingDonationData.categoryDescription + ' ' + sub.subCategoryDescription}</td>
                            <td style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>{formatINR(sub.amount)}</td>
                            <td style={{ border: '1px solid #ddd', padding: '6px' }}>
                              {sub.receiptNo || (sub.noReceipt ? 'ரசீது இல்லை' : '-')}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '6px' }}>
                              {sub.isSelfSponsored ? '✅' : '❌'}
                            </td>
                          </tr>
                        ))}
                        <tr style={{ fontWeight: 'bold', background: '#f9f9f9' }}>
                          <td colSpan="1" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>மொத்தம் </td>
                          <td colSpan="1" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}>₹{pendingDonationData.subcategories.reduce((sum, sub) => sum + (sub.amount || 0), 0).toLocaleString('ta-IN')}</td>
                          <td colSpan="2" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'right' }}></td>
                        </tr>
                        <tr style={{ fontWeight: 'bold', background: '#f9f9f9' }}>
                          <td colSpan="4" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>குடும்ப எண் : {pendingDonationData.familyId}</td>
                        </tr>
                        <tr style={{ fontWeight: 'bold', background: '#f9f9f9' }}>
                          <td colSpan="4" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>பணம் செலுத்தியது : {pendingDonationData.familyMemberFirstName} {pendingDonationData.familyMemberLastName}</td>
                        </tr>
                        <tr style={{ fontWeight: 'bold', background: '#f9f9f9' }}>
                          <td colSpan="4" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>தேதி :  {new Date(pendingDonationData.date).toLocaleDateString('ta-IN')}</td>
                        </tr>
                        <tr style={{ fontWeight: 'bold', background: '#f9f9f9' }}>
                          <td colSpan="4" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>பரிவர்த்தனை முறை :  {pendingDonationData.transactionMode}</td>
                        </tr>
                        <tr style={{ fontWeight: 'bold', background: '#f9f9f9' }}>
                          <td colSpan="4" style={{ border: '1px solid #ddd', padding: '6px', textAlign: 'left' }}>குறிப்பு : {pendingDonationData.externalTransactionReference}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  {selfSponsoredWarning && (
                    <p style={{ color: 'red', marginTop: '1rem' }}>
                      ⚠️ சில துணை வகைகள் தனியுரிமையாக செலுத்த அனுமதிக்கின்றன, ஆனால் நீங்கள் தேர்வு செய்யவில்லை. நீங்கள் தொடர விரும்புகிறீர்களா?
                    </p>
                  )}
                  <div style={{ marginTop: '1.5rem' }}>
                    <button
                      onClick={async () => {
                        setShowConfirmationModal(false);
                        setLoading(true);
                        try {
                          const response = await createDonation(pendingDonationData);
                          if (response === 409) {
                            alert("❗ Donation already exists.");
                            navigate(`/donation/view/${familyId}`);
                            return;
                          }

                          if (response) {
                            alert("✅ Donation added successfully!");
                            navigate(`/donation/view/${familyId}`);
                          }
                        } catch (err) {
                          console.error("Error adding donation:", err);
                          alert("⚠️ Something went wrong while adding the donation.");
                        } finally {
                          setLoading(false);
                          setPendingDonationData(null);
                        }
                      }}
                      style={{ marginRight: '1rem' }}
                    >
                      ஆம், தொடரவும்
                    </button>
                    <button onClick={() => setShowConfirmationModal(false)}>இல்லை, திரும்பவும்</button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div >

      <Footer />
    </>
  );
};

export default DonationCreate;
